/**
 * Session Storage Service
 * 
 * Provides utilities for persisting component state across page navigation
 * within the same browser session. Uses sessionStorage which persists until
 * the browser tab/window is closed.
 */

/**
 * Save state to sessionStorage with a namespace prefix
 * 
 * @param {string} namespace - Namespace for the component (e.g., 'sell', 'research')
 * @param {string} key - Key for the specific state
 * @param {any} value - Value to store (will be JSON.stringified)
 */
export const saveToSession = (namespace, key, value) => {
    try {
        const storageKey = `${namespace}_${key}`;
        const serializedValue = JSON.stringify(value);
        sessionStorage.setItem(storageKey, serializedValue);
    } catch (error) {
        console.warn(`Failed to save ${namespace}_${key} to session storage:`, error);
    }
};

/**
 * Load state from sessionStorage with a namespace prefix
 * 
 * @param {string} namespace - Namespace for the component (e.g., 'sell', 'research')
 * @param {string} key - Key for the specific state
 * @param {any} defaultValue - Default value if nothing is found
 * @returns {any} Parsed value from storage or defaultValue
 */
export const loadFromSession = (namespace, key, defaultValue = null) => {
    try {
        const storageKey = `${namespace}_${key}`;
        const serializedValue = sessionStorage.getItem(storageKey);
        
        if (serializedValue === null) {
            return defaultValue;
        }
        
        return JSON.parse(serializedValue);
    } catch (error) {
        console.warn(`Failed to load ${namespace}_${key} from session storage:`, error);
        return defaultValue;
    }
};

/**
 * Clear all session storage for a specific namespace
 * 
 * @param {string} namespace - Namespace to clear (e.g., 'sell', 'research')
 */
export const clearSessionNamespace = (namespace) => {
    try {
        // Get all keys and remove those that match the namespace
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(`${namespace}_`)) {
                keysToRemove.push(key);
            }
        }
        
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (error) {
        console.warn(`Failed to clear session storage for namespace ${namespace}:`, error);
    }
};

/**
 * Clear all session storage data for the application
 */
export const clearAllSessionStorage = () => {
    try {
        sessionStorage.clear();
    } catch (error) {
        console.warn('Failed to clear all session storage:', error);
    }
};

/**
 * Session storage utilities for the Sell component
 */
export const sellSessionStorage = {
    // Form inputs
    saveUsername: (username) => saveToSession('sell', 'username', username),
    loadUsername: () => loadFromSession('sell', 'username', ''),
    
    saveGeeklistId: (geeklistId) => saveToSession('sell', 'geeklistId', geeklistId),
    loadGeeklistId: () => loadFromSession('sell', 'geeklistId', ''),
    
    savePricePercentage: (percentage) => saveToSession('sell', 'pricePercentage', percentage),
    loadPricePercentage: () => loadFromSession('sell', 'pricePercentage', 80),
    
    // Price rounding settings
    saveRoundingMode: (mode) => saveToSession('sell', 'roundingMode', mode),
    loadRoundingMode: () => loadFromSession('sell', 'roundingMode', 'down'),
    
    saveRoundingIncrement: (increment) => saveToSession('sell', 'roundingIncrement', increment),
    loadRoundingIncrement: () => loadFromSession('sell', 'roundingIncrement', 1),
    
    saveIsConfigPanelOpen: (isOpen) => saveToSession('sell', 'isConfigPanelOpen', isOpen),
    loadIsConfigPanelOpen: () => loadFromSession('sell', 'isConfigPanelOpen', false),
    
    saveIsActionsPanelOpen: (isOpen) => saveToSession('sell', 'isActionsPanelOpen', isOpen),
    loadIsActionsPanelOpen: () => loadFromSession('sell', 'isActionsPanelOpen', false),
    
    saveIsCollectionParamsOpen: (isOpen) => saveToSession('sell', 'isCollectionParamsOpen', isOpen),
    loadIsCollectionParamsOpen: () => loadFromSession('sell', 'isCollectionParamsOpen', false),
    
    // Default price type and BIN multiplier
    saveDefaultPriceType: (type) => saveToSession('sell', 'defaultPriceType', type),
    loadDefaultPriceType: () => loadFromSession('sell', 'defaultPriceType', 'FP'),
    
    saveDefaultBinMultiplier: (multiplier) => saveToSession('sell', 'defaultBinMultiplier', multiplier),
    loadDefaultBinMultiplier: () => loadFromSession('sell', 'defaultBinMultiplier', 1.2),
    
    // Global BBCode for VFM links
    saveGlobalBbCode: (bbCode) => saveToSession('sell', 'globalBbCode', bbCode),
    loadGlobalBbCode: () => loadFromSession('sell', 'globalBbCode', ''),
    
    // Collection parameters
    saveCollectionParams: (params) => saveToSession('sell', 'collectionParams', params),
    loadCollectionParams: () => loadFromSession('sell', 'collectionParams', {
        own: true,
        trade: true,
        version: true,
        excludesubtype: 'boardgameaccessory'
    }),
    
    // Data state
    saveSellerItems: (items) => saveToSession('sell', 'sellerItems', items),
    loadSellerItems: () => loadFromSession('sell', 'sellerItems', []),
    
    // Loading states
    saveIsLoadingCollection: (isLoading) => saveToSession('sell', 'isLoadingCollection', isLoading),
    loadIsLoadingCollection: () => loadFromSession('sell', 'isLoadingCollection', false),
    
    saveLoadingMessage: (message) => saveToSession('sell', 'loadingMessage', message),
    loadLoadingMessage: () => loadFromSession('sell', 'loadingMessage', ''),
    
    saveIsLoadingPrices: (isLoading) => saveToSession('sell', 'isLoadingPrices', isLoading),
    loadIsLoadingPrices: () => loadFromSession('sell', 'isLoadingPrices', false),
    
    savePriceProgress: (progress) => saveToSession('sell', 'priceProgress', progress),
    loadPriceProgress: () => loadFromSession('sell', 'priceProgress', { current: 0, total: 0 }),
    
    // Clear all sell component state
    clearAll: () => clearSessionNamespace('sell')
};

/**
 * Session storage utilities for the Research component
 */
export const researchSessionStorage = {
    // Search state
    saveSearchQuery: (query) => saveToSession('research', 'searchQuery', query),
    loadSearchQuery: () => loadFromSession('research', 'searchQuery', ''),
    
    saveSearchResults: (results) => saveToSession('research', 'searchResults', results),
    loadSearchResults: () => loadFromSession('research', 'searchResults', []),
    
    saveIsSearching: (isSearching) => saveToSession('research', 'isSearching', isSearching),
    loadIsSearching: () => loadFromSession('research', 'isSearching', false),
    
    saveSelectedGame: (game) => saveToSession('research', 'selectedGame', game),
    loadSelectedGame: () => loadFromSession('research', 'selectedGame', null),
    
    saveIsModalOpen: (isOpen) => saveToSession('research', 'isModalOpen', isOpen),
    loadIsModalOpen: () => loadFromSession('research', 'isModalOpen', false),
    
    saveRowCount: (count) => saveToSession('research', 'rowCount', count),
    loadRowCount: () => loadFromSession('research', 'rowCount', 0),
    
    // Clear all research component state
    clearAll: () => clearSessionNamespace('research')
};

const sessionStorageService = {
    saveToSession,
    loadFromSession,
    clearSessionNamespace,
    clearAllSessionStorage,
    sellSessionStorage,
    researchSessionStorage
};

export default sessionStorageService;